import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { APP_FOOTER_CONFIG } from './app-footer.config';
import { COMPANY_INFO } from './company-info';
import { resolveFooterLinks } from './footer-links';

/**
 * 로그인 후(사이드바 있는) 앱 화면용 압축 푸터 — 사이드바가 있을 때 셸이
 * `<app-footer />` 대신 렌더한다(`app-shell.component.ts`의 `footerVariant` 참고).
 *
 * 법정 고지 항목(사업자등록번호·통신판매업 신고번호 등, `COMPANY_INFO`)은 전부 그대로
 * 유지한다 — "접어놓고 클릭해야 보이는" 형태 대신 항상 보이는 한 줄(줄바꿈 허용)로만
 * 압축한다(2026-08-14 논의: 통신판매업 신고번호가 상시 표시 의무 항목이라 완전히
 * 숨기거나 클릭 전까지 안 보이게 하는 건 부적절). 3열 그리드 마케팅 푸터는 사이드바
 * 없는 랜딩 화면에만 남는다.
 */
@Component({
  selector: 'app-footer-compact',
  imports: [RouterLink],
  template: `
    <footer
      class="border-t border-gray-200 bg-white text-xs text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400"
    >
      <div class="flex w-full flex-wrap items-center gap-x-2 gap-y-1 px-4 py-3 sm:px-6 lg:px-8">
        <span>{{ companyInfo.name }}</span>
        <span aria-hidden="true">·</span>
        <span>{{ companyInfo.ceo }}</span>
        <span aria-hidden="true">·</span>
        <span>{{ companyInfo.callNumber }}</span>
        <span aria-hidden="true">·</span>
        <span>{{ companyInfo.businessRegistrationNumber }}</span>
        <span aria-hidden="true">·</span>
        <span>{{ companyInfo.mailOrderBusinessNumber }}</span>
        <span aria-hidden="true">·</span>
        <a
          [href]="companyInfo.addressMapUrl"
          target="_blank"
          rel="noopener"
          class="hover:text-gray-700 dark:hover:text-gray-300 no-underline"
          >{{ companyInfo.address }}</a
        >
        @if (links().length > 0) {
          <span aria-hidden="true">·</span>
        }
        @for (link of links(); track link.label; let last = $last) {
          @if (link.routerLink !== null && link.routerLink !== undefined) {
            <a [routerLink]="link.routerLink" class="hover:text-gray-700 dark:hover:text-gray-300 no-underline">{{
              link.label
            }}</a>
          } @else {
            <a [href]="link.href" class="hover:text-gray-700 dark:hover:text-gray-300 no-underline">{{ link.label }}</a>
          }
          @if (!last) {
            <span aria-hidden="true">·</span>
          }
        }
        <span class="ml-auto text-gray-500 dark:text-gray-400">Copyright © {{ companyInfo.name }}</span>
      </div>
    </footer>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppFooterCompactComponent {
  private readonly config = inject(APP_FOOTER_CONFIG, { optional: true }) ?? {};

  protected readonly companyInfo = COMPANY_INFO;
  protected readonly links = computed(() => resolveFooterLinks(this.config));
}
